import styled from '@emotion/styled'
import { ulBuilding08 } from '@faceup/icons/ulBuilding08'
import { ulFolder } from '@faceup/icons/ulFolder'
import { ulGraduationHat02 } from '@faceup/icons/ulGraduationHat02'
import { ulInbox01 } from '@faceup/icons/ulInbox01'
import { ulMail01 } from '@faceup/icons/ulMail01'
import { ulPhoneCall01 } from '@faceup/icons/ulPhoneCall01'
import { ulPhoneCall02 } from '@faceup/icons/ulPhoneCall02'
import { ulTable } from '@faceup/icons/ulTable'
import { ulUsers01 } from '@faceup/icons/ulUsers01'
import { useMatchPath } from '@faceup/router'
import {
  AdminMenu,
  type AdminMenuItem,
  PageContentSkeleton,
  AdminLayout as UiAdminLayout,
  ucAiPhone,
} from '@faceup/ui'
import { FuAdminRoleType } from '@faceup/utils'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import { routes } from '../hooks/useRoutes'
import HeaderProfile from './HeaderProfile'
import RegionSelect from './RegionSelect'

const logo = '/logo/U-logo.svg'

const useActiveRoute = () => {
  const matchPath = useMatchPath()

  if (matchPath(routes.schools)) {
    return 'schools'
  }

  if (matchPath(routes.companies)) {
    return 'companies'
  }

  if (matchPath(routes.partners) || matchPath(routes.partner)) {
    return 'partners'
  }

  if (matchPath(routes.reports) || matchPath(routes.report)) {
    return 'reports'
  }

  if (matchPath(routes.materials)) {
    return 'materials'
  }

  if (matchPath(routes.globalTables)) {
    return 'global-tables'
  }

  if (matchPath(routes.hotlines)) {
    return 'hotlines'
  }

  if (matchPath(routes.vonageHotlines)) {
    return 'vonage-hotlines'
  }

  if (matchPath(routes.sendEmails)) {
    return 'send-emails'
  }

  if (matchPath(routes.aiHotlineSystemInstructions)) {
    return 'ai-hotline-system-instructions'
  }

  return ''
}

const fragments = {
  FuAdminLayout_admin: graphql(`
    fragment FuAdminLayout_admin on FuAdmin {
      id
      name
      role
      companyCountries
      schoolCountries
      ...HeaderProfile_user
    }
  `),
}

type Props = {
  children?: React.ReactNode
  logout: () => void
  viewer: FragmentType<typeof fragments.FuAdminLayout_admin> | null
}

const AdminLayout = ({ viewer: _viewer, logout, children }: Props) => {
  const viewer = getFragmentData(fragments.FuAdminLayout_admin, _viewer)
  const activeKey = useActiveRoute()

  // mock loading instead of spinner
  if (!viewer?.id) {
    return (
      <UiAdminLayout
        menu={
          <AdminMenu
            logo={logo}
            homepageLink={routes => routes.home()}
            items={[]}
            activeKey={activeKey}
            logoBackground='#cccccc'
          />
        }
      >
        <PageContentSkeleton />
      </UiAdminLayout>
    )
  }

  const menu: AdminMenuItem[] = [
    {
      key: 'companies',
      label: 'Companies',
      icon: ulBuilding08,
      link: routes => routes.companies(),
      visible: Boolean(viewer?.companyCountries?.length),
    },
    {
      key: 'schools',
      label: 'Schools',
      icon: ulGraduationHat02,
      link: routes => routes.schools(),
      visible: Boolean(viewer?.schoolCountries?.length),
    },
    {
      key: 'partners',
      label: 'Partners',
      icon: ulUsers01,
      link: routes => routes.partners(),
      visible:
        Boolean(viewer?.companyCountries?.length) && viewer?.role !== FuAdminRoleType.Psychologist,
    },
    {
      key: 'reports',
      label: 'Reports',
      icon: ulInbox01,
      link: routes => routes.reports(),
      visible: viewer?.role === FuAdminRoleType.Psychologist,
    },
    {
      key: 'materials',
      label: 'Materials',
      icon: ulFolder,
      link: routes => routes.materials(),
      visible: viewer?.role !== FuAdminRoleType.Psychologist,
    },
    {
      key: 'hotlines',
      label: 'Hotlines',
      icon: ulPhoneCall01,
      link: routes => routes.hotlines(),
      visible: viewer?.role === FuAdminRoleType.Manager || viewer?.role === FuAdminRoleType.Admin,
    },
    {
      key: 'vonage-hotlines',
      label: 'Vonage Hotlines',
      icon: ulPhoneCall02,
      link: routes => routes.vonageHotlines(),
      visible: viewer?.role === FuAdminRoleType.Manager || viewer?.role === FuAdminRoleType.Admin,
    },
    {
      key: 'ai-hotline-system-instructions',
      label: 'AI Hotline Config',
      icon: ucAiPhone,
      link: routes => routes.aiHotlineSystemInstructions(),
      visible: viewer?.role === FuAdminRoleType.Manager || viewer?.role === FuAdminRoleType.Admin,
    },
    {
      key: 'send-emails',
      label: 'Send emails',
      icon: ulMail01,
      link: routes => routes.sendEmails(),
      visible: viewer?.role === FuAdminRoleType.Admin,
    },
    {
      key: 'global-tables',
      label: 'Global tables',
      icon: ulTable,
      link: routes => routes.globalTables(),
      visible: viewer?.role === FuAdminRoleType.Admin,
    },
  ]

  return (
    <UiAdminLayout
      menu={
        <AdminMenu
          logo={logo}
          homepageLink={routes => routes.home()}
          items={menu}
          activeKey={activeKey}
          logoBackground='#fd9f13'
        />
      }
    >
      <Header>
        <RegionSelect />
        <HeaderProfile logout={() => logout()} viewer={viewer} />
      </Header>
      {children}
    </UiAdminLayout>
  )
}

const Header = styled.div`
  background: #fff;
  padding: var(--ant-padding-sm);
  display: flex;
  justify-content: flex-end;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid var(--ant-color-border-secondary);
  gap: var(--ant-padding-sm);
`

export default AdminLayout
